import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Baby, 
  ChevronRight, ArrowRight, CheckCircle, LogIn, UserPlus, Sparkles, User
} from 'lucide-react';
import { cmsAPI, inscriptionsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const defaultInscriptionTypes = [
  {
    id: 'enfant',
    title: 'Enfant / Adolescent',
    subtitle: 'De 4 à 17 ans',
    description: 'Inscription pour un enfant ou adolescent aux cours d\'anglais. Formulaire avec informations parentales et autorisations.',
    icon: Baby,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    link: '/inscription/enfant',
    features: [
      'Programmes Kiddoes (4-6 ans)',
      'Cambridge Young Learners (7-12 ans)',
      'Préparation examens Cambridge (13-17 ans)'
    ]
  }
];

const ChoixInscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cmsContent, setCmsContent] = useState(null);
  const [inscriptionTypes] = useState(defaultInscriptionTypes);
  const [previousInscriptions, setPreviousInscriptions] = useState([]);

  useEffect(() => {
    loadCmsContent();
    if (user) {
      // Fetch this user's previous inscriptions to propose re-inscription
      inscriptionsAPI.list()
        .then((r) => setPreviousInscriptions(r.data || []))
        .catch(() => setPreviousInscriptions([]));
    }
  }, [user]);

  const loadCmsContent = async () => {
    try {
      const res = await cmsAPI.getSections('form-choix');
      if (res.data && res.data.length > 0) {
        const intro = res.data.find(s => s.section_key === 'intro');
        setCmsContent(intro);
      }
    } catch (error) {
      console.error('Error loading CMS content:', error);
    }
  };

  // Aggregate unique children (across previous inscriptions) for prefill
  const previousEnfants = (() => {
    const map = new Map();
    previousInscriptions.forEach((i) => {
      (i.enfants || []).forEach((e) => {
        const key = `${e.prenom}|${e.nom}|${e.date_naissance}`;
        if (!map.has(key)) map.set(key, { ...e, fromInscription: i });
      });
    });
    return Array.from(map.values());
  })();

  const reinscrireEnfant = (enfant) => {
    const inscription = enfant.fromInscription || {};
    const prefill = {
      enfants: [{ ...enfant }],
      adresse: inscription.adresse || '',
      pere: inscription.pere || null,
      mere: inscription.mere || null,
    };
    navigate('/inscription/enfant', { state: { prefill } });
  };

  const reinscrireAdulte = () => {
    navigate('/inscription/adulte', { state: { prefill: { user } } });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#2C3C65] to-[#1e2d4d] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {cmsContent?.title || 'Inscription aux cours d\'anglais'}
              </h1>
              <p className="text-lg md:text-xl text-blue-200 max-w-2xl mx-auto">
                {cmsContent?.subtitle || 'Choisissez le type d\'inscription qui correspond à votre situation'}
              </p>
              {cmsContent?.content && (
                <p className="text-base text-blue-100 max-w-2xl mx-auto mt-4">
                  {cmsContent.content}
                </p>
              )}
            </motion.div>
          </div>
        </section>

        {/* Smart account banner */}
        <section className="-mt-8 mb-4 relative z-10">
          <div className="max-w-5xl mx-auto px-4">
            {!user ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-xl border border-blue-100 p-6"
                data-testid="account-prompt-card"
              >
                <div className="flex items-start gap-3 mb-4">
                  <Sparkles className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold text-[#2C3C65]">Vous avez déjà un compte&nbsp;?</h3>
                    <p className="text-sm text-[#6777A1]">
                      Connectez-vous pour pré-remplir automatiquement vos informations et celles de vos enfants.
                    </p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Link
                    to="/login"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-[#285BBB] hover:bg-[#1e47a0] text-white font-semibold rounded-lg transition-colors"
                    data-testid="prompt-login"
                  >
                    <LogIn className="h-5 w-5" />
                    Se connecter
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-[#285BBB] text-[#285BBB] hover:bg-[#285BBB] hover:text-white font-semibold rounded-lg transition-colors"
                    data-testid="prompt-register"
                  >
                    <UserPlus className="h-5 w-5" />
                    Créer un compte
                  </Link>
                </div>
                <p className="text-xs text-[#6777A1] text-center mt-3">
                  Vous pouvez aussi continuer en tant qu'invité en choisissant un type d'inscription ci-dessous.
                </p>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-6"
                data-testid="user-prefill-card"
              >
                <div className="flex items-start gap-3 mb-4">
                  <CheckCircle className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#2C3C65]">Bonjour {user.prenom}&nbsp;!</h3>
                    <p className="text-sm text-[#6777A1]">
                      {previousEnfants.length > 0
                        ? `Sélectionnez un élève déjà inscrit pour pré-remplir le formulaire de l'année suivante, ou commencez une nouvelle inscription ci-dessous.`
                        : `Vos informations seront pré-remplies automatiquement.`}
                    </p>
                  </div>
                </div>

                {previousEnfants.length > 0 && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {previousEnfants.map((e, i) => (
                      <button
                        key={i}
                        onClick={() => reinscrireEnfant(e)}
                        className="flex items-center gap-3 p-3 border-2 border-[#F5F7FF] hover:border-[#285BBB] rounded-lg text-left transition-colors group"
                        data-testid={`prefill-enfant-${i}`}
                      >
                        <div className="w-10 h-10 bg-[#F5F7FF] group-hover:bg-[#285BBB] group-hover:text-white text-[#285BBB] rounded-full flex items-center justify-center transition-colors">
                          <User className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#2C3C65] truncate">{e.prenom} {e.nom}</p>
                          <p className="text-xs text-[#6777A1]">Réinscrire pour l'année suivante</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-[#6777A1] group-hover:text-[#285BBB] transition-colors" />
                      </button>
                    ))}
                  </div>
                )}

                {user.role === 'eleve' && previousEnfants.length === 0 && (
                  <button
                    onClick={reinscrireAdulte}
                    className="w-full flex items-center justify-center gap-2 p-3 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#1e47a0] transition-colors"
                    data-testid="prefill-adulte"
                  >
                    Réinscription adulte (pré-remplissage)
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </motion.div>
            )}
          </div>
        </section>

        {/* Choix du type d'inscription */}
        <section className="py-8 md:py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-1 max-w-2xl mx-auto gap-6 md:gap-8">
              {inscriptionTypes.map((type, index) => (
                <motion.div
                  key={type.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={type.link}
                    className={`block h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 ${type.borderColor} hover:border-transparent group`}
                  >
                    {/* Header avec icône */}
                    <div className={`bg-gradient-to-r ${type.color} p-6 text-white`}>
                      <div className="flex items-center justify-between">
                        <type.icon className="h-12 w-12" />
                        <ArrowRight className="h-6 w-6 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all" />
                      </div>
                      <h2 className="text-2xl font-bold mt-4">{type.title}</h2>
                      <p className="text-white/80 text-sm mt-1">{type.subtitle}</p>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <p className="text-[#6777A1] mb-6">
                        {type.description}
                      </p>

                      {/* Features */}
                      <ul className="space-y-3 mb-6">
                        {type.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-[#2C3C65]">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <div className={`${type.bgColor} rounded-lg p-4 text-center group-hover:bg-opacity-80 transition-colors`}>
                        <span className="font-semibold text-[#2C3C65] flex items-center justify-center gap-2">
                          {type.id === 'entreprise' ? 'Demander un devis' : 'Commencer l\'inscription'}
                          <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Info supplémentaire */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-12 text-center"
            >
              <div className="bg-white rounded-xl shadow-md p-6 md:p-8 max-w-3xl mx-auto">
                <h3 className="text-xl font-bold text-[#2C3C65] mb-3">
                  Besoin d'aide pour choisir ?
                </h3>
                <p className="text-[#6777A1] mb-4">
                  Notre équipe est disponible pour vous conseiller et vous orienter vers la formation la plus adaptée à vos besoins.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center justify-center px-6 py-3 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#1e4a9a] transition-colors"
                  >
                    Nous contacter
                  </Link>
                  <a 
                    href="tel:+33626716022"
                    className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#285BBB] text-[#285BBB] font-semibold rounded-lg hover:bg-[#285BBB] hover:text-white transition-colors"
                  >
                    06 26 71 60 22
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ChoixInscriptionPage;
